import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { InterventionFormTypes } from "./data";
import { InterventionsDocumentationForm } from "../../../forms/interventions-forms/InterventionsDocumentationForm";
import { ReactSVG } from "react-svg";
import html2pdf from "html2pdf.js";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import { sendEmailWithPdf, uploadPdf } from "../../../../redux/modules/patient/actions";
import { getPharmacyInfo } from "../../../forms/common/getPharmacyInfo";
import { displayToast } from "../../../../redux/modules/toast/actions";

const InterventionForm = ({ intervention, formType, onBack = () => {}, hideIconButton = false }) => {
  const { id: patientId, interventionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patient } = useSelector((state) => state.patients);
  const { currentLoggedInUserDetail: userFromCurrent } = useSelector((state) => state.currentLoggedInUserDetail ?? {});
  const { currentLoggedInUserDetail: userFromUsers, pharmacyStores } = useSelector((state) => state.users ?? {});
  const pharmacy = getPharmacyInfo(userFromCurrent ?? userFromUsers, pharmacyStores) ?? {};
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Print/Email actions (standardized to match Minor Ailments & Medication Review documentation forms)
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const printButtonRef = useRef(null);
  const printableRef = useRef(null);

  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  if (!intervention || !patient) {
    return <div>Loading</div>;
  }

  function handleNextPage() {
    if (currentPage >= totalPages) return;
    setCurrentPage(currentPage + 1);
  }

  function handlePreviousPage() {
    if (currentPage <= 1) return;
    setCurrentPage(currentPage - 1);
  }

  function handlePrint() {
    if (!printableRef.current) {
      window.print();
      return;
    }

    const html = printableRef.current.innerHTML;
    const newWin = window.open("", "_blank");
    if (!newWin) {
      window.print();
      return;
    }

    // Preserve current styles in the new print window
    const links = Array.from(
      document.querySelectorAll('link[rel="stylesheet"]')
    )
      .map((l) => l.outerHTML)
      .join("\n");
    const styles = Array.from(document.querySelectorAll("style"))
      .map((s) => s.outerHTML)
      .join("\n");

    newWin.document.open();
    newWin.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Print</title>${links}${styles}<style>@page{size: letter portrait; margin: 0.5in;} body{margin:0; padding:0; -webkit-print-color-adjust:exact; print-color-adjust:exact;} .printable-area{width:8.5in; margin:0 auto;} .printable-area *{max-height:none !important;} </style></head><body>`
    );
    newWin.document.write(
      `<div class="print-container printable-area">${html}</div>`
    );
    newWin.document.write("</body></html>");
    newWin.document.close();
    newWin.focus();

    setTimeout(() => {
      try {
        newWin.print();
      } catch (err) {
        console.error("Print failed", err);
      }
      try {
        newWin.close();
      } catch (e) {}
    }, 500);
  }

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  async function handleSendEmail() {
    const normalizedEmail = (email || "").trim();
    if (!normalizedEmail) {
      setEmailError("Please enter recipient email");
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!printableRef.current) {
      setEmailError("Document is not ready to send yet");
      return;
    }

    try {
      setLoading(true);
      setEmailError("");

      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          filename: `${formType || "document"}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        })
        .from(printableRef.current)
        .outputPdf("blob");

      const pdfFilename = `${formType || "document"}.pdf`;
      const pdfLink = await dispatch(uploadPdf(pdfBlob, pdfFilename));

      const formData = new FormData();
      formData.append("File", pdfBlob, pdfFilename);
      formData.append("ToEmail", normalizedEmail);

      const subject = intervention?.clinicalServiceType || "Intervention";
      formData.append("Subject", subject);
      formData.append(
        "PatientName",
        `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim()
      );
      formData.append("FormName", subject);
      formData.append("PharmacyName", pharmacy.pharmacyName || "");
      formData.append("StoreNumber", pharmacy.pharmacyStoreNumber || "");
      formData.append("PhoneNumber", pharmacy.pharmacyPhone || "");
      formData.append("FaxNumber", pharmacy.pharmacyFax || "");
      formData.append("StoreEmail", pharmacy.pharmacyEmail || "");
      formData.append("PdfLink", pdfLink);

      await dispatch(sendEmailWithPdf(formData));
      dispatch(
        displayToast("Email sent successfully", "success", { autoClose: 4000 })
      );

      setShowEmailModal(false);
      setEmail("");
    } catch (error) {
      setEmailError(error?.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  }

  // Get intervention assessment form data
  const formRoot = intervention?.interventionAssessment?.interventionAssessmentForm || {};
  const preAssessment = formRoot?.preAssessment || {};
  const clinicalAssessment = formRoot?.clinicalAssessment || {};
  const documentation = formRoot?.documentation || {};
  const interventionDetails = formRoot?.interventionDetails || {};
  const pharmacistFollowUp = formRoot?.pharmacistFollowUp || {};
  const prescriberFollowUp = formRoot?.prescriberFollowUp || {};
  const interventionType = intervention?.interventionType || null;
  const startDateTime = formRoot?.startDateTime || intervention?.createdDate || new Date().toISOString();
  const endDateTime = formRoot?.endDateTime || intervention?.lastModifiedDate || new Date().toISOString();

  // Render form component based on formType
  let formComponent = <div>No Form Found!</div>;
  
  // For now, use InterventionsDocumentationForm for all intervention form types
  switch (formType) {
    case InterventionFormTypes.InterventionDocumentation:
    case InterventionFormTypes.Notes:
    case InterventionFormTypes.PharmaceuticalOpinion:
    case InterventionFormTypes.Billing:
    case InterventionFormTypes.Adaptation:
    case InterventionFormTypes.GeneralIntervention:
      formComponent = (
        <InterventionsDocumentationForm
          formType={formType}
          patient={patient}
          preAssessment={preAssessment}
          clinicalAssessment={clinicalAssessment}
          clinicalServiceName={intervention?.clinicalServiceType || ""}
          clinicalServiceTypeCode={intervention?.clinicalServiceTypeCode || ""}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          documentation={formRoot?.notification}
          interventionDetails={interventionDetails}
          intervention={intervention}
          pharmacistFollowUp={pharmacistFollowUp}
          prescriberFollowUp={prescriberFollowUp}
          interventionType={intervention?.interventionType || null}
        />
      );
      break;
    default:
      onBack();
      break;
  }

  return (
    <div className="p-9 flex flex-row gap-6 w-full justify-between items-start">
      {!hideIconButton && (
        <div className="xl:min-w-[180px]">
          <button
            onClick={() => onBack()}
            aria-label="Back"
            className="flex items-center justify-center"
          >
            <ReactSVG
              src="/icons/back-arrow.svg"
              beforeInjection={(svg) => {
                svg.setAttribute("class", "w-16 h-16");
              }}
            />
          </button>
        </div>
      )}
      <div className="relative">
        {currentPage > 1 && (
          <button
            className="absolute -left-[40px] top-1/2"
            onClick={handlePreviousPage}
          >
            <svg
              width="46"
              height="23"
              viewBox="0 0 46 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.1667 20.3333L2 11.1667M2 11.1667L11.1667 2M2"
                stroke="#0B4C5B"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <div className="overflow-hidden w-[8.5in] min-h-[11in]" ref={printableRef}>
          {formComponent}
        </div>
        {currentPage < totalPages && (
          <button
            className="absolute -right-[40px] top-1/2"
            onClick={handleNextPage}
          >
            <svg
              width="46"
              height="23"
              viewBox="0 0 46 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              transform="scale(-1, 1)"
            >
              <path
                d="M11.1667 20.3333L2 11.1667M2 11.1667L11.1667 2M2"
                stroke="#0B4C5B"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      {!hideIconButton && (
        <>
          <div className="flex flex-col xl:flex-row bg-white print:hidden">
            <button
              aria-label="Print"
              ref={printButtonRef}
              onClick={() => handlePrint()}
              className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
            >
              <ReactSVG
                src="/icons/printer-outline.svg"
                beforeInjection={(svg) => {
                  svg.setAttribute("class", "w-9 h-9");
                }}
              />
              <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">
                Print
              </span>
            </button>

            <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />

            <button
              aria-label="Email"
              onClick={() => {
                setEmailError("");
                setShowEmailModal(true);
              }}
              className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
            >
              <ReactSVG
                src="/icons/mail-open.svg"
                beforeInjection={(svg) => {
                  svg.setAttribute("class", "w-9 h-9");
                }}
              />
              <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4">
                Email
              </span>
            </button>

            <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />

            <button
              aria-label="Fax"
              className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
            >
              <ReactSVG
                src="/icons/fax-outline.svg"
                beforeInjection={(svg) => {
                  svg.setAttribute("class", "w-9 h-9");
                }}
              />
              <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-[85%] hidden group-hover:block bg-gray-20 text-gray-800 shadow rounded py-2 px-4 whitespace-nowrap">
                <span className="font-semibold">Email Fax</span> - Coming Soon
              </span>
            </button>
          </div>

          <ConfirmationModalNew
            open={showEmailModal}
            title={"Send Email"}
            className={"!w-[400px] !max-w-[400px] !p-0"}
            content={() => (
              <div className="p-2">
                <input
                  type="email"
                  placeholder="Enter recipient email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  className="w-full border border-gray-300 p-2 rounded mb-2"
                />
                {emailError && (
                  <p className="text-sm text-red-600 mb-3">{emailError}</p>
                )}
              </div>
            )}
            handleClose={() => {
              setShowEmailModal(false);
              setEmailError("");
            }}
            onCross={() => {
              setShowEmailModal(false);
              setEmailError("");
            }}
            confirmText={loading ? "Sending..." : "Send"}
            cancelText={"Cancel"}
            confirmAction={handleSendEmail}
            cancelAction={() => {
              setShowEmailModal(false);
              setEmailError("");
            }}
            disableConfirm={loading}
            hideCloseIcon={false}
          />
        </>
      )}
    </div>
  );
};

export default InterventionForm;